/**
 * CP-3.3 — Availability calendar (month list view).
 *
 * Lets a contractor add blocks of kind available / unavailable / partial / tentative
 * with day_part full|am|pm|custom. Overlapping available/unavailable blocks are
 * rejected by the backend (409); tentative/partial blocks may overlap.
 * Times are entered in the contractor's chosen timezone but stored UTC.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

const KIND_COLOR = {
  available: "bg-emerald-100 text-emerald-800 border-emerald-300",
  unavailable: "bg-rose-100 text-rose-800 border-rose-300",
  partial: "bg-amber-100 text-amber-800 border-amber-300",
  tentative: "bg-sky-100 text-sky-800 border-sky-300",
};

function fmtLocal(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  try {
    return d.toLocaleString("en-CA", { timeZone: tz, year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return d.toLocaleString();
  }
}

function localInputToUtcIso(localValue, tz) {
  // <input type="datetime-local"> returns "YYYY-MM-DDTHH:mm" — interpret as the user's TZ.
  if (!localValue) return null;
  // Build a Date that represents this wall-time in tz.
  // Trick: parse into UTC parts then adjust by tz offset.
  const dt = new Date(`${localValue}:00Z`);
  const tzOffset = (() => {
    try {
      const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, timeZoneName: "shortOffset",
      });
      const parts = dtf.formatToParts(new Date());
      const off = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+0";
      const m = off.match(/GMT([+-]?)(\d{1,2})(?::?(\d{2}))?/);
      if (!m) return 0;
      const sign = m[1] === "-" ? -1 : 1;
      const h = parseInt(m[2] || "0", 10);
      const mi = parseInt(m[3] || "0", 10);
      return sign * (h * 60 + mi);
    } catch { return 0; }
  })();
  return new Date(dt.getTime() - tzOffset * 60 * 1000).toISOString();
}

export function Availability() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [profile, setProfile] = useState({});
  const [draft, setDraft] = useState({
    kind: "available", day_part: "full",
    starts_at: "", ends_at: "", note: "", share_with_crew: false,
  });

  const tz = profile.timezone || "UTC";

  const refresh = async () => {
    setLoading(true);
    try {
      const [me, mine] = await Promise.all([
        api("/me/contractor-profile"),
        api("/me/availability"),
      ]);
      setProfile(me.profile || {});
      setBlocks(mine.data || []);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const saveTz = async () => {
    setBusy(true);
    try {
      await api("/me/contractor-profile", { method: "PUT", body: { timezone: profile.timezone || "UTC" } });
      toast.success("Timezone saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addBlock = async () => {
    const starts_at = localInputToUtcIso(draft.starts_at, tz);
    const ends_at = localInputToUtcIso(draft.ends_at, tz);
    if (!starts_at || !ends_at) { toast.error("Start and end required"); return; }
    setBusy(true);
    try {
      await api("/me/availability", {
        method: "POST",
        body: { kind: draft.kind, day_part: draft.day_part, starts_at, ends_at,
                note: draft.note || null, share_with_crew: !!draft.share_with_crew },
      });
      toast.success("Block added");
      setDraft({ kind: "available", day_part: "full", starts_at: "", ends_at: "", note: "", share_with_crew: false });
      await refresh();
    } catch (e) {
      if (e.problem?.code === "OVERLAP") toast.error("Overlaps an existing available/unavailable block");
      else toast.error(e.message);
    } finally { setBusy(false); }
  };

  const deleteBlock = async (id) => {
    setBusy(true);
    try {
      await api(`/me/availability/${id}`, { method: "DELETE" });
      setBlocks((cur) => cur.filter((b) => b.id !== id));
      toast.success("Block removed");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const sortedBlocks = useMemo(() =>
    [...blocks].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)),
    [blocks]);

  if (loading) return <Layout><div className="avcp-card p-6">Loading availability…</div></Layout>;

  return (
    <Layout>
      <div data-testid={TID.availRoot}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Availability</h1>
          <p className="text-[var(--avcp-ink-soft)] mt-1">
            Manage when you can be booked. Available / unavailable blocks cannot overlap each other;
            partial and tentative blocks are allowed to overlap.
          </p>
        </div>

        <section className="avcp-card p-6 mb-6">
          <h2 className="text-lg font-semibold mb-3">Timezone</h2>
          <div className="flex items-center gap-2">
            <input className="avcp-input flex-1 max-w-md" data-testid={TID.availTz}
                   placeholder="e.g. America/Vancouver"
                   value={profile.timezone || ""}
                   onChange={(e) => setProfile((p) => ({ ...p, timezone: e.target.value }))} />
            <button className="avcp-btn primary" data-testid={TID.availSaveTz}
                    disabled={busy} onClick={saveTz}>Save timezone</button>
          </div>
          <p className="text-xs text-[var(--avcp-ink-soft)] mt-2">
            Availability is stored in UTC and displayed in your timezone. Default UTC.
          </p>
        </section>

        <section className="avcp-card p-6 mb-6" data-testid={TID.availOpenAdd}>
          <h2 className="text-lg font-semibold mb-3">Add a block</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <label className="grid gap-1">
              <span className="text-sm">Kind</span>
              <select className="avcp-input" data-testid={TID.availKind} value={draft.kind}
                      onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value }))}>
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
                <option value="partial">Partial</option>
                <option value="tentative">Tentative</option>
              </select>
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Day part</span>
              <select className="avcp-input" data-testid={TID.availDayPart} value={draft.day_part}
                      onChange={(e) => setDraft((d) => ({ ...d, day_part: e.target.value }))}>
                <option value="full">Full day</option>
                <option value="am">AM</option>
                <option value="pm">PM</option>
                <option value="custom">Custom hours</option>
              </select>
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Starts ({tz})</span>
              <input type="datetime-local" className="avcp-input" data-testid={TID.availStart}
                     value={draft.starts_at}
                     onChange={(e) => setDraft((d) => ({ ...d, starts_at: e.target.value }))} />
            </label>
            <label className="grid gap-1">
              <span className="text-sm">Ends ({tz})</span>
              <input type="datetime-local" className="avcp-input" data-testid={TID.availEnd}
                     value={draft.ends_at}
                     onChange={(e) => setDraft((d) => ({ ...d, ends_at: e.target.value }))} />
            </label>
            <label className="grid gap-1 md:col-span-2">
              <span className="text-sm">Note (private unless shared)</span>
              <input className="avcp-input" data-testid={TID.availNote} value={draft.note}
                     onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} />
            </label>
            <label className="flex items-center gap-2 md:col-span-2 text-sm">
              <input type="checkbox" data-testid={TID.availShare} checked={!!draft.share_with_crew}
                     onChange={(e) => setDraft((d) => ({ ...d, share_with_crew: e.target.checked }))} />
              Share note with my crew (still hidden from clients)
            </label>
          </div>
          <button className="avcp-btn primary mt-4" data-testid={TID.availSave}
                  disabled={busy} onClick={addBlock}>Add block</button>
        </section>

        <section className="avcp-card p-6">
          <h2 className="text-lg font-semibold mb-3">Your blocks</h2>
          <ul className="space-y-2" data-testid={TID.availList}>
            {sortedBlocks.length === 0 && (
              <li className="text-sm text-[var(--avcp-ink-soft)]">No blocks yet. Add one above.</li>
            )}
            {sortedBlocks.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-3 p-3 rounded border border-[var(--avcp-line)]"
                  data-testid={`${TID.availBlock}-${b.id}`}>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs border ${KIND_COLOR[b.kind] || ""}`}>{b.kind}</span>
                  <span className="text-xs text-[var(--avcp-ink-soft)]">{b.day_part}</span>
                  <div className="text-sm">
                    <div>{fmtLocal(b.starts_at, tz)} → {fmtLocal(b.ends_at, tz)}</div>
                    {b.note && <div className="text-xs text-[var(--avcp-ink-soft)]">&ldquo;{b.note}&rdquo;{b.share_with_crew && " · shared"}</div>}
                  </div>
                </div>
                <button className="avcp-btn ghost" data-testid={`${TID.availDelete}-${b.id}`}
                        onClick={() => deleteBlock(b.id)}>Remove</button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Layout>
  );
}

export default Availability;
